import { useRef, useCallback } from 'react';
import { useCurrency } from '@/hooks/useCurrency';
import { isElectron } from '@/lib/subdomain';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  unit_price: number;
  total: number;
  notes?: string | null;
  modifiers?: Array<{ id: string; name: string; price: number }> | unknown;
  status: string;
}

interface Order {
  id: string;
  order_number: string;
  order_type: string;
  status: string;
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  tip_amount: number;
  total: number;
  notes?: string | null;
  guest_count: number;
  created_at: string;
  completed_at?: string | null;
  table?: { name: string } | null;
  customer?: { first_name: string | null; last_name: string | null } | null;
  server?: { first_name: string | null; last_name: string | null } | null;
  items?: OrderItem[];
}

interface LocationInfo {
  name: string;
  address?: {
    street?: string;
    line1?: string;
    city?: string;
    parish?: string;
    state?: string;
  } | null;
  phone?: string | null;
}

interface ReceiptPrintOptions {
  order: Order;
  location?: LocationInfo | null;
  isReprint?: boolean;
  printNumber?: number;
  /** When true, prints a prominent TRAINING MODE label on the receipt */
  isTrainingMode?: boolean;
}

// Parse modifiers safely
const getModifiers = (item: OrderItem): Array<{ name: string; price: number }> => {
  if (!item.modifiers) return [];
  if (Array.isArray(item.modifiers)) {
    return item.modifiers as Array<{ name: string; price: number }>;
  }
  return [];
};

// Generate receipt HTML
export function generateReceiptHTML(options: ReceiptPrintOptions, formatCurrency: (amount: number) => string): string {
  const { order, location, isReprint, printNumber, isTrainingMode } = options;

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const orderTypeLabels: Record<string, string> = {
    dine_in: 'Dine-In',
    takeout: 'Takeout',
    delivery: 'Delivery',
    bar_tab: 'Bar Tab',
    online: 'Online',
  };

  const items = order.items || [];
  const customerName = order.customer
    ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
    : null;
  const serverName = order.server
    ? `${order.server.first_name || ''} ${order.server.last_name || ''}`.trim()
    : null;

  const locationAddress = location?.address
    ? `${location.address.street || location.address.line1 || ''}<br>${location.address.city || ''}${location.address.city && (location.address.parish || location.address.state) ? ', ' : ''}${location.address.parish || location.address.state || ''}`
    : '';

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Receipt - Order #${order.order_number}</title>
  <style>
    @page {
      size: 80mm auto;
      margin: 0;
    }
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: 'Courier New', monospace;
      font-size: 12px;
      line-height: 1.4;
      width: 80mm;
      padding: 8mm 4mm;
      background: white;
      color: black;
    }
    .header {
      text-align: center;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px dashed #000;
    }
    .store-name {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    .store-info {
      font-size: 10px;
      color: #333;
    }
    .reprint-badge {
      background: #f00;
      color: white;
      padding: 2px 8px;
      margin-top: 8px;
      display: inline-block;
      font-weight: bold;
    }
    .training-badge {
      background: #d97706;
      color: white;
      padding: 4px 12px;
      margin-top: 8px;
      margin-bottom: 4px;
      display: inline-block;
      font-weight: bold;
      font-size: 14px;
      letter-spacing: 2px;
    }
    .order-info {
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px dashed #000;
    }
    .order-number {
      font-size: 18px;
      font-weight: bold;
      text-align: center;
      margin-bottom: 8px;
    }
    .info-row {
      display: flex;
      justify-content: space-between;
      margin: 2px 0;
    }
    .items {
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 1px dashed #000;
    }
    .item {
      margin: 8px 0;
    }
    .item-header {
      display: flex;
      justify-content: space-between;
    }
    .item-name {
      font-weight: bold;
    }
    .item-qty {
      font-weight: bold;
    }
    .item-modifiers {
      font-size: 10px;
      padding-left: 16px;
      color: #333;
    }
    .item-notes {
      font-size: 10px;
      padding-left: 16px;
      font-style: italic;
      color: #666;
    }
    .item-voided {
      text-decoration: line-through;
      color: #999;
    }
    .totals {
      margin-bottom: 12px;
    }
    .total-row {
      display: flex;
      justify-content: space-between;
      margin: 2px 0;
    }
    .total-row.grand-total {
      font-size: 16px;
      font-weight: bold;
      border-top: 2px solid #000;
      padding-top: 8px;
      margin-top: 8px;
    }
    .footer {
      text-align: center;
      margin-top: 16px;
      padding-top: 8px;
      border-top: 1px dashed #000;
      font-size: 10px;
    }
    .thank-you {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 4px;
    }
    @media print {
      body {
        width: 80mm;
      }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="store-name">${location?.name || 'Restaurant'}</div>
    ${locationAddress ? `<div class="store-info">${locationAddress}</div>` : ''}
    ${location?.phone ? `<div class="store-info">${location.phone}</div>` : ''}
    ${isReprint ? `<div class="reprint-badge">** REPRINT ${printNumber ? `#${printNumber}` : ''} **</div>` : ''}
    ${isTrainingMode ? `<div class="training-badge">** TRAINING MODE **</div>` : ''}
  </div>

  <div class="order-info">
    <div class="order-number">Order #${order.order_number}</div>
    <div class="info-row">
      <span>Date:</span>
      <span>${formatDate(order.created_at)}</span>
    </div>
    <div class="info-row">
      <span>Time:</span>
      <span>${formatTime(order.created_at)}</span>
    </div>
    <div class="info-row">
      <span>Type:</span>
      <span>${orderTypeLabels[order.order_type] || order.order_type}</span>
    </div>
    ${order.table?.name ? `
    <div class="info-row">
      <span>Table:</span>
      <span>${order.table.name}</span>
    </div>
    ` : ''}
    ${serverName ? `
    <div class="info-row">
      <span>Server:</span>
      <span>${serverName}</span>
    </div>
    ` : ''}
    ${customerName ? `
    <div class="info-row">
      <span>Customer:</span>
      <span>${customerName}</span>
    </div>
    ` : ''}
  </div>

  <div class="items">
    ${items.map(item => {
      const modifiers = getModifiers(item);
      const isVoided = item.status === 'voided';
      return `
      <div class="item ${isVoided ? 'item-voided' : ''}">
        <div class="item-header">
          <span><span class="item-qty">${item.quantity}x</span> <span class="item-name">${item.name}</span>${isVoided ? ' (VOID)' : ''}</span>
          <span>${formatCurrency(item.total)}</span>
        </div>
        ${modifiers.length > 0 ? `
        <div class="item-modifiers">
          ${modifiers.map(mod => `+ ${mod.name}${mod.price > 0 ? ` (+${formatCurrency(mod.price)})` : ''}`).join('<br>')}
        </div>
        ` : ''}
        ${item.notes ? `<div class="item-notes">Note: ${item.notes}</div>` : ''}
      </div>
      `;
    }).join('')}
  </div>

  <div class="totals">
    <div class="total-row">
      <span>Subtotal:</span>
      <span>${formatCurrency(order.subtotal)}</span>
    </div>
    ${order.discount_amount > 0 ? `
    <div class="total-row">
      <span>Discount:</span>
      <span>-${formatCurrency(order.discount_amount)}</span>
    </div>
    ` : ''}
    <div class="total-row">
      <span>Tax:</span>
      <span>${formatCurrency(order.tax_amount)}</span>
    </div>
    ${order.tip_amount > 0 ? `
    <div class="total-row">
      <span>Tip:</span>
      <span>${formatCurrency(order.tip_amount)}</span>
    </div>
    ` : ''}
    <div class="total-row grand-total">
      <span>TOTAL:</span>
      <span>${formatCurrency(order.total)}</span>
    </div>
  </div>

  <div class="footer">
    ${isTrainingMode ? `<div class="training-badge">** TRAINING MODE **</div>` : ''}
    <div class="thank-you">Thank You!</div>
    <div>Please come again</div>
    ${isTrainingMode ? `<div style="margin-top: 8px; font-size: 10px; color: #d97706; font-weight: bold;">This is not a real receipt. No sale was recorded.</div>` : ''}
  </div>
</body>
</html>
  `;
}

// Check if auto-print is enabled (stored in localStorage)
function isAutoPrintEnabled(): boolean {
  try {
    return localStorage.getItem('blazepos-auto-print') === 'true';
  } catch {
    return false;
  }
}

// Set auto-print preference
export function setAutoPrint(enabled: boolean): void {
  try {
    localStorage.setItem('blazepos-auto-print', enabled ? 'true' : 'false');
  } catch {
    // Ignore localStorage errors
  }
}

// Get auto-print preference
export function getAutoPrint(): boolean {
  return isAutoPrintEnabled();
}

// Hook for printing receipts
export function useReceiptPrint() {
  const { formatCurrency } = useCurrency();
  const printWindowRef = useRef<Window | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const printReceipt = useCallback(async (options: ReceiptPrintOptions) => {
    const html = generateReceiptHTML(options, formatCurrency);

    // In Electron with auto-print enabled, use silent printing
    if (isElectron() && isAutoPrintEnabled()) {
      const electronAPI = (window as any).electronAPI;
      if (electronAPI?.printer?.silentPrint) {
        try {
          const result = await electronAPI.printer.silentPrint(html);
          if (result.success) {
            console.log('Receipt printed silently');
            return true;
          } else {
            console.error('Silent print failed:', result.error);
            // Fall back to dialog print
          }
        } catch (error) {
          console.error('Silent print error:', error);
          // Fall back to dialog print
        }
      }
    }

    // In Electron, use iframe-based printing (popup windows are blocked)
    if (isElectron()) {
      // Create or reuse an iframe for printing
      if (!iframeRef.current) {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-10000px';
        iframe.style.left = '-10000px';
        iframe.style.width = '320px';
        iframe.style.height = '600px';
        document.body.appendChild(iframe);
        iframeRef.current = iframe;
      }

      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        console.error('Failed to access iframe document');
        return false;
      }

      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
      }, 250);

      return true;
    }

    // Web: Open a new window for printing
    const printWindow = window.open('', '_blank', 'width=320,height=600');
    if (!printWindow) {
      console.error('Failed to open print window');
      return false;
    }

    printWindowRef.current = printWindow;
    printWindow.document.write(html);
    printWindow.document.close();

    // Use setTimeout to ensure content is rendered before printing
    // onload doesn't reliably fire with document.write() in many browsers
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      // Close after printing (or if print is cancelled)
      setTimeout(() => {
        printWindow.close();
      }, 1000);
    }, 250);

    return true;
  }, [formatCurrency]);

  return { printReceipt };
}

